import { useCallback } from 'react';
import { useAppSelector } from '@/store/reduxHooks';
import { getNotificationSound } from '@/constants/notificationSounds';
import { getSharedAudio } from '@/services/sound/audioUnlock';

interface IUsePlaySound {
  play: () => void;
  playSound: (soundId: string, volume?: number) => void;
}

/**
 * Хук воспроизведения звука уведомления.
 * Читает настройки звука (вкл/выкл, мелодия, громкость) из стора.
 * `play` — проигрывает активную мелодию с учётом флага `enabled`
 * (для события нового заказа). `playSound` — принудительно проигрывает
 * указанную мелодию независимо от `enabled` (для превью в настройках).
 *
 * Использует общий аудио-инстанс приложения (`getSharedAudio`), который один
 * раз разблокируется на первый жест пользователя (см. `audioUnlock`). После
 * этого браузер разрешает воспроизведение по событию сокета всегда, включая
 * фоновую и свёрнутую вкладку. Ошибку воспроизведения тихо глотаем.
 */
export const usePlaySound = (): IUsePlaySound => {
  const { enabled, soundId, volume } = useAppSelector(
    (state) => state.soundSettings
  );

  const playSound = useCallback(
    (targetSoundId: string, targetVolume = volume): void => {
      const sound = getNotificationSound(targetSoundId);
      const audio = getSharedAudio();

      audio.src = sound.src;
      audio.volume = Math.min(1, Math.max(0, targetVolume));
      audio.currentTime = 0;
      audio.play().catch(() => {});
    },
    [volume]
  );

  const play = useCallback((): void => {
    if (!enabled) {
      return;
    }

    playSound(soundId, volume);
  }, [enabled, soundId, volume, playSound]);

  return { play, playSound };
};
